import React, { useState } from 'react';
import inventoryService from '../../services/inventoryService';
import { useStore } from '../../context/StoreContext';

const HandheldCycleCountPage = () => {
  const { activeStore } = useStore();
  const [lookup, setLookup] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [countQty, setCountQty] = useState('');
  const [note, setNote] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionItems, setSessionItems] = useState([]);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const findProduct = async () => {
    if (!activeStore?._id) {
      setFeedback({ type: 'error', message: 'Select a store to start counting.' });
      return;
    }
    if (!lookup.trim()) {
      setFeedback({ type: 'error', message: 'Enter or scan a SKU / barcode.' });
      return;
    }
    try {
      const data = await inventoryService.searchProducts(activeStore._id, lookup.trim());
      const match =
        data.find(
          (p) =>
            p.barcode?.toLowerCase() === lookup.trim().toLowerCase() ||
            p.sku?.toLowerCase() === lookup.trim().toLowerCase()
        ) || data[0];
      if (!match) {
        setFeedback({ type: 'error', message: 'Product not found.' });
        setCurrentProduct(null);
        return;
      }
      setCurrentProduct(match);
      setCountQty(match.stock ?? 0);
      setFeedback({ type: '', message: '' });
    } catch (err) {
      console.error(err);
      setFeedback({ type: 'error', message: 'Failed to lookup product.' });
    }
  };

  const recordCount = async () => {
    if (!activeStore?._id || !currentProduct) return;
    const newQuantity = Number(countQty);
    if (Number.isNaN(newQuantity)) {
      setFeedback({ type: 'error', message: 'Enter a valid quantity.' });
      return;
    }
    const reason = [
      sessionName ? `Stocktake count (${sessionName.trim()})` : 'Stocktake count',
      note.trim(),
    ]
      .filter(Boolean)
      .join(': ');
    try {
      await inventoryService.adjustStock(currentProduct._id, {
        storeId: activeStore._id,
        newQuantity,
        reason,
      });
      const entry = {
        productName: currentProduct.name,
        oldQuantity: currentProduct.stock ?? 0,
        newQuantity,
        timestamp: new Date().toISOString(),
      };
      setSessionItems((prev) => [entry, ...prev]);
      setCurrentProduct({ ...currentProduct, stock: newQuantity });
      setCountQty('');
      setLookup('');
      setNote('');
      setFeedback({ type: 'success', message: 'Count recorded.' });
    } catch (err) {
      console.error(err);
      setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to record count.' });
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Session name</label>
          <input
            type="text"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            placeholder="Optional reference e.g. Aisle 3"
            className="w-full border border-gray-300 rounded-xl px-3 py-2 mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Scan or search</label>
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2"
              placeholder="Barcode / SKU / name"
            />
            <button
              type="button"
              onClick={findProduct}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800"
            >
              Find
            </button>
          </div>
        </div>
        {feedback.message && (
          <p className={feedback.type === 'error' ? 'text-sm text-red-600' : 'text-sm text-green-600'}>
            {feedback.message}
          </p>
        )}
      </div>

      {currentProduct && (
        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Counting</p>
            <h3 className="text-xl font-semibold">{currentProduct.name}</h3>
            <p className="text-sm text-slate-500">Recorded stock: {currentProduct.stock ?? 0}</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700">Counted quantity</label>
            <input
              type="number"
              value={countQty}
              onChange={(e) => setCountQty(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700">Notes</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Bin cleared, damaged stock"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </div>
          <button
            type="button"
            onClick={recordCount}
            className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
          >
            Save count
          </button>
        </div>
      )}

      {sessionItems.length > 0 && (
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs uppercase tracking-[0.4em] text-slate-400 mb-2">Recent counts</p>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {sessionItems.map((item, idx) => (
              <div key={`${item.productName}-${idx}`} className="border rounded-xl p-3">
                <p className="font-semibold">{item.productName}</p>
                <p className="text-sm text-slate-500">
                  {item.oldQuantity} → {item.newQuantity} •{' '}
                  {new Date(item.timestamp).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HandheldCycleCountPage;
